import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import i18n from '../i18n';
import { getAllLanguages, LanguageResponse } from '../services/languageService';

export interface LanguageOption {
    code: string;
    name: string;
}

interface LanguageContextValue {
    lang: string;
    available: LanguageOption[];
    ready: boolean;
    changeLanguage: (code: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const useLanguage = (): LanguageContextValue => {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
    return ctx;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const [lang, setLang] = useState<string>(() => {
        try {
            const stored = localStorage.getItem('app_lang');
            return stored || navigator.language.split('-')[0] || 'en';
        } catch (e) {
            return 'en';
        }
    });

    const [available, setAvailable] = useState<LanguageOption[]>([]);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let mounted = true;

        (async () => {
            // Load available language codes from backend via languageService
            try {
                const resp = await getAllLanguages();
                const langs: LanguageOption[] = (resp.languages || []).map((l: LanguageResponse) => ({
                    code: (l.code || (l.language as string) || '').toString(),
                    name: (l.display_name || l.name || l.language || l.code) as string,
                }));
                if (mounted) setAvailable(langs);
            } catch (e) {
                // If backend fails, provide sensible defaults (en, si)
                if (mounted) setAvailable([{ code: 'en', name: 'English' }, { code: 'si', name: 'සිංහල' }]);
            }

            // Load translation bundle from frontend local JSON
            try {
                // dynamic import of local JSON file under src/locales/{lang}/translation.json
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                const bundle = await import(`../locales/${lang}/translation.json`);
                const resources = bundle.default || bundle;
                i18n.addResourceBundle(lang, 'translation', resources, true, true);
                await i18n.changeLanguage(lang);
                if (mounted) setReady(true);
            } catch (err) {
                // fallback to English if loading fails
                try {
                    const bundle = await import(`../locales/en/translation.json`);
                    const resources = bundle.default || bundle;
                    i18n.addResourceBundle('en', 'translation', resources, true, true);
                    await i18n.changeLanguage('en');
                    if (mounted) {
                        setLang('en');
                        setReady(true);
                    }
                } catch (err2) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to load any locale bundles', err2);
                    if (mounted) setReady(true);
                }
            }
        })();

        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeLanguage = async (code: string) => {
        try {
            // Try to load local bundle
            try {
                const bundle = await import(`../locales/${code}/translation.json`);
                const resources = bundle.default || bundle;
                i18n.addResourceBundle(code, 'translation', resources, true, true);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Local bundle not found for', code, e);
                // will still attempt to change language to code; keys will fallback to fallbackLng
            }

            await i18n.changeLanguage(code);
            setLang(code);
            try {
                localStorage.setItem('app_lang', code);
            } catch (_) { }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to change language', err);
        }
    };

    const value: LanguageContextValue = {
        lang,
        available,
        ready,
        changeLanguage,
    };

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageProvider;
